/** @file TilePrompts: the Tile screen's questions: unsaved strokes on close, and the fit of a dropped image. */
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { BulkFitPrompt } from '../../panels/art/BulkFitPrompt';
import { Button } from '../../ui-kit/Button';
import { Modal } from '../../ui-kit/Modal';
import { tileDrafts } from './tileDrafts';
import { tileFitImage } from './tileFitImage';

/** This tab's draft key, slot key and size; Save (resolves whether it committed) and the tab's close. */
export type TilePromptsProps = {
  draftKey: string; slot: string; size: readonly [number, number];
  onSave: () => Promise<boolean>; onClose: () => void;
};

/**
 * Close: Save (then close when it committed), Discard (drop the strokes and close) or Cancel (the backdrop and Esc
 * too). Fit: the Art fit prompt (Pad / Scale / Skip) for an image of another size; Pad or Scale replaces the canvas
 * as one undo step.
 */
export function TilePrompts({ draftKey, slot, size, onSave, onClose }: TilePromptsProps): ReactElement | null {
  const prompt = useStore(tileDrafts, (s) => (s.prompt?.key === draftKey ? s.prompt : null));
  const drafts = tileDrafts.getState();
  if (prompt === null) return null;
  const cancel = (): void => { drafts.setPrompt(null); };
  if (prompt.kind === 'fit') {
    const misfit = { name: prompt.name, slot, png: new Uint8Array(), size: [prompt.image.width, prompt.image.height] as [number, number], slotSize: [size[0], size[1]] as [number, number] };
    return (
      <BulkFitPrompt misfits={[misfit]} choose={(fit) => {
        cancel();
        if (fit === null) return;
        const fitted = tileFitImage(prompt.image, size, fit);
        drafts.stroke(draftKey);
        drafts.edit(draftKey, (img) => { img.data.set(fitted.data); return true; });
      }} />
    );
  }
  const save = async (): Promise<void> => {
    if (!(await onSave())) return;
    drafts.discard(draftKey);
    onClose();
  };
  const discard = (): void => { drafts.discard(draftKey); onClose(); };
  return (
    <Modal title="Unsaved strokes" onClose={cancel} footer={<>
      <Button variant="primary" onClick={() => { void save(); }}>Save</Button>
      <Button variant="danger" onClick={discard}>Discard</Button>
      <Button onClick={cancel}>Cancel</Button>
    </>}>
      <p>Tile {slot} has strokes that are not saved. Save them to the puppet before closing the tab?</p>
    </Modal>
  );
}
